import type { UXCamConfiguration } from "cordova-uxcam";
import { routes } from "../route/constants";

// cordova-uxcam UXCamOcclusionType.OccludeAllTextFields — occlude every text
// field on the listed screens. Hardcoded (rather than imported from
// `cordova-uxcam`) so pulling in the config on web/Cypress doesn't trigger the
// package's `window.UXCam = ...` backward-compat side effect. UXCam.test.ts
// asserts this value against the real enum so a renumber fails CI.
const OCCLUDE_ALL_TEXT_FIELDS = 3;

/**
 * The class UXCam scans the WebView DOM for and blanks out in replays.
 *
 * This — not the `occlusions` config below — is what actually masks passwords
 * in a Capacitor app: `OccludeAllTextFields` occludes *native* text widgets,
 * and REFSIX has none (the whole UI is a WebView), so HTML `<input>`s are only
 * hidden when they carry this class. Put it on every element that can display
 * a secret, including fields that flip to `type="text"` behind a
 * show-password toggle.
 *
 * @see https://developer.uxcam.com/docs/sensitive-views-inside-webviews
 */
export const UXCAM_OCCLUDE_CLASS = "uxcam-occlude";

/**
 * `enableImprovedScreenCapture` is read by the Android native config
 * (cordova-uxcam's UXCamPlugin.kt) but is missing from the 3.8.2 TypeScript
 * definitions, so widen the type rather than drop a setting that is doing work.
 * (iOS ignores it — its SDK captures the WebView without the flag.)
 */
type RefsixUXCamConfiguration = UXCamConfiguration & {
    enableImprovedScreenCapture?: boolean;
};

export const UXCamConfig: RefsixUXCamConfiguration = {
    userAppKey: process.env.REACT_APP_UX_CAM_KEY ?? "",
    enableAutomaticScreenNameTagging: false,
    enableImprovedScreenCapture: true,
    // REFSIX-776: native text-field occlusion on the two screens where a
    // password is typed. Strictly belt-and-braces on top of
    // UXCAM_OCCLUDE_CLASS — it only reaches native inputs the WebView delegates
    // to (autofill sheets, native alert inputs); the class is what covers the
    // HTML fields.
    //
    // Screen names are the router pathnames tagged by PageViewAnalytics.
    // Since REFSIX-796 that includes the INITIAL route of a page load, so
    // this scoping also covers the returning user who lands on /login and
    // types their password without navigating anywhere
    // (cypress/e2e/uxcam_session_lifecycle.cy.ts asserts the landing screen
    // is tagged). Even so, do NOT rely on this for password masking — the
    // class is what occludes the HTML fields.
    occlusions: [
        {
            type: OCCLUDE_ALL_TEXT_FIELDS,
            screens: [routes.register, routes.login],
        },
    ],
};
